import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  CsvUploadResponse,
  CsvValidationResponse,
  CustomFieldDefinition,
  DedupCandidateListResponse,
  DedupCandidateStatus,
  DedupResolutionItem,
  ImportAddressFixListResponse,
  ImportColumnMappingTargetKind,
  ImportEntityType,
  ImportJob,
  ImportJobListItem,
  ImportJobStatus,
  ImportReviewDecision,
  ImportRevalidateRowRequest,
  ImportRevalidateRowResponse,
  ImportRowError,
  ImportRetryNormalizationResponse,
  DiagnoseImportResponse,
  DiagnoseBatchResponse,
  FileImportTicketResponse,
} from "@adva/types";

interface ColumnMappingInput {
  source_header: string;
  target_kind: ImportColumnMappingTargetKind;
  target_field: string | null;
}

export type CustomFieldType = CustomFieldDefinition["field_type"];
export type CustomFieldWidget = NonNullable<CustomFieldDefinition["ui_widget"]>;

interface BulkFieldDefinitionInput {
  entity_type: string;
  field_key: string;
  label: string;
  field_type: CustomFieldType;
  ui_widget?: CustomFieldWidget;
  is_required?: boolean;
  description?: string;
}

interface ImportEntity {
  entity_type: ImportEntityType;
  tier: number;
  description: string;
}

interface ImportEntitiesResponse {
  entities: ImportEntity[];
}

// ─── ADV-633: import job list + detail hooks ─────────────────────────────────

interface ImportJobsParams {
  status?: ImportJobStatus | "all";
  entity_type?: ImportEntityType | "all";
  limit?: number;
  offset?: number;
}

interface ImportJobsResponse {
  jobs: ImportJobListItem[];
  total: number;
}

interface ImportJobResponse {
  job: ImportJob;
}

interface UseImportJobsOptions {
  refetchInterval?: number | false;
}

function buildImportQuery(params: ImportJobsParams): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== "" && v !== "all",
  );
  if (entries.length === 0) return "";
  return (
    "?" +
    entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join("&")
  );
}

export function useImportJobs(
  params: ImportJobsParams = {},
  options: UseImportJobsOptions = {},
) {
  return useQuery({
    queryKey: ["import", "jobs", params],
    queryFn: () =>
      api.get<ImportJobsResponse>(`/import/jobs${buildImportQuery(params)}`),
    refetchInterval: options.refetchInterval,
  });
}

// useQuery's `refetchInterval` accepts a number, false, or a function whose
// argument carries the full Query generics. We forward the consumer's option
// to useQuery verbatim — typing it as the same union from TanStack's options
// avoids importing `Query` from query-core directly.
type RefetchIntervalOption =
  UseQueryOptions<ImportJobResponse>["refetchInterval"];

interface UseImportJobOptions {
  refetchInterval?: RefetchIntervalOption;
}

export function useImportJob(
  id: string | null,
  options: UseImportJobOptions = {},
) {
  return useQuery({
    queryKey: ["import", "jobs", id],
    enabled: !!id,
    queryFn: () => api.get<ImportJobResponse>(`/import/jobs/${id}`),
    refetchInterval: options.refetchInterval,
  });
}

// ADV-938: cancel an in-flight import job. Idempotent at the API layer — the
// service rejects cancellation against terminal jobs with a 409, which the
// mutation surfaces to the caller. After a successful cancel, both the list
// and detail queries are invalidated so the badge re-renders to `cancelled`.
interface CancelImportJobResponse {
  id: string;
  status: ImportJobStatus;
  cancelled_at: string;
  cancelled_reason: string | null;
}

export function useCancelImportJob(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason?: string) =>
      api.post<CancelImportJobResponse>(
        `/import/jobs/${jobId}/cancel`,
        reason ? { reason } : {},
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["import", "jobs"] });
      queryClient.invalidateQueries({ queryKey: ["import", "jobs", jobId] });
    },
  });
}

// ─── ADV-1157: retry normalization + AI diagnosis surfaces ───────────────────

export function useRetryNormalization(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      api.post<ImportRetryNormalizationResponse>(
        `/import/jobs/${jobId}/retry-normalization`,
        {},
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["import", "jobs"] });
      queryClient.invalidateQueries({ queryKey: ["import", "jobs", jobId] });
    },
  });
}

/** Manual-trigger only (no auto-fire) — POSTs to the diagnose endpoint. The
 * server caches the diagnosis for the life of the job and replays it; pass
 * `{ refresh: true }` (the "Re-run analysis" affordance) to force a fresh run. */
export function useDiagnoseImport(jobId: string) {
  return useMutation({
    mutationFn: async (vars?: { refresh?: boolean }) =>
      api.post<DiagnoseImportResponse>(
        `/import/jobs/${jobId}/diagnose${vars?.refresh ? "?refresh=true" : ""}`,
        {},
      ),
  });
}

// ─── ADV-1018: batch-grain diagnosis surfaces ───────────────────────────────

/** Manual-trigger batch diagnose — POSTs to the batch endpoint and returns
 * the aggregated diagnosis + service-computed batch fields (blocking_tier,
 * clusters[], review_url). The server caches the diagnosis for the life of the
 * batch and replays it; pass `{ refresh: true }` ("Re-run analysis") to force a
 * fresh run. Same monthly cap as per-job. */
export function useDiagnoseBatch(batchId: string | null) {
  return useMutation({
    mutationFn: async (vars?: { refresh?: boolean }) => {
      if (!batchId) throw new Error("No batch id");
      return api.post<DiagnoseBatchResponse>(
        `/import/batches/${batchId}/diagnose${vars?.refresh ? "?refresh=true" : ""}`,
        {},
      );
    },
  });
}

interface BatchStatusResponse {
  child_count: number;
  all_terminal: boolean;
  all_completed_no_errors: boolean;
  any_errors: boolean;
  any_awaiting_review: boolean;
  first_awaiting_review_job_id: string | null;
}

/** Polling hook for the restore-from-backup flow (Phase 4). Hits
 * `GET /import/batches/:batchId/status` every `refetchInterval` ms (default 3 s
 * with a soft 5 min ceiling enforced by the consumer) until `all_terminal`. */
export function useBatchStatus(
  batchId: string | null,
  options: { refetchInterval?: number | false } = {},
) {
  return useQuery({
    queryKey: ["import", "batches", batchId, "status"],
    enabled: !!batchId,
    queryFn: () =>
      api.get<BatchStatusResponse>(`/import/batches/${batchId}/status`),
    refetchInterval: options.refetchInterval,
  });
}

export function useFileImportTicket(jobId: string) {
  return useMutation({
    mutationFn: async (input: { title: string; body: string }) =>
      api.post<FileImportTicketResponse>(
        `/import/jobs/${jobId}/file-ticket`,
        input,
      ),
  });
}

// ─── ADV-668: review screen detail data + decision mutations ─────────────────

type PaginationParams = {
  limit?: number;
  offset?: number;
};

interface ImportJobErrorsResponse {
  errors: ImportRowError[];
  total: number;
  limit: number;
  offset: number;
}

function buildPaginationQuery(
  params: Record<string, string | number | undefined>,
): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== "",
  );
  if (entries.length === 0) return "";
  return (
    "?" +
    entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join("&")
  );
}

export function useImportJobErrors(
  id: string | null,
  params: PaginationParams = {},
) {
  return useQuery({
    queryKey: ["import", "jobs", id, "errors", params],
    enabled: !!id,
    queryFn: () =>
      api.get<ImportJobErrorsResponse>(
        `/import/jobs/${id}/errors${buildPaginationQuery(params)}`,
      ),
  });
}

export function useImportJobAddressFixes(
  id: string | null,
  params: PaginationParams = {},
) {
  return useQuery({
    queryKey: ["import", "jobs", id, "address-fixes", params],
    enabled: !!id,
    queryFn: () =>
      api.get<ImportAddressFixListResponse>(
        `/import/jobs/${id}/address-fixes${buildPaginationQuery(params)}`,
      ),
  });
}

type DedupCandidatesParams = PaginationParams & {
  status?: DedupCandidateStatus;
};

export function useImportJobDedupCandidates(
  id: string | null,
  params: DedupCandidatesParams = {},
) {
  return useQuery({
    queryKey: ["import", "jobs", id, "dedup-candidates", params],
    enabled: !!id,
    queryFn: () =>
      api.get<DedupCandidateListResponse>(
        `/import/jobs/${id}/dedup-candidates${buildPaginationQuery(params)}`,
      ),
  });
}

/**
 * Paginate to completion across all dedup candidates for a job. The dedup
 * endpoint caps at limit=200; this helper loops until it has collected `total`
 * candidates, concatenating each page's candidates.
 *
 * Termination is guaranteed two ways: (1) a page returning zero candidates
 * stops the loop — `offset` advances by the page length, so an empty page can
 * never make progress and would otherwise re-fetch the same offset forever
 * (off-by-one total, transient empty page, or overstated total); (2) a
 * malformed response whose `total` is not a finite number throws rather than
 * silently exiting after page 1 with a partial set.
 */
export async function fetchAllImportJobDedupCandidates(
  id: string,
  params: DedupCandidatesParams = {},
): Promise<DedupCandidateListResponse["candidates"]> {
  const PAGE_SIZE = 200;
  const all: DedupCandidateListResponse["candidates"] = [];
  let offset = 0;
  let total = 0;
  do {
    const page = await api.get<DedupCandidateListResponse>(
      `/import/jobs/${id}/dedup-candidates${buildPaginationQuery({ ...params, limit: PAGE_SIZE, offset })}`,
    );
    if (!Number.isFinite(page.total)) {
      throw new Error(
        `dedup-candidates response for job ${id} returned a non-finite total: ${String(page.total)}`,
      );
    }
    all.push(...page.candidates);
    total = page.total;
    offset += page.candidates.length;
    // A zero-candidate page can never advance offset; stop instead of looping
    // forever on the same offset.
    if (page.candidates.length === 0) break;
  } while (offset < total && all.length < total);
  return all;
}

interface ResolveDedupCandidatesResult {
  resolved: number;
  resolutions: { candidate_id: string; status: string }[];
}

export function useResolveDedupCandidates(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (resolutions: DedupResolutionItem[]) => {
      return api.post<ResolveDedupCandidatesResult>(
        `/import/jobs/${jobId}/dedup-candidates/resolve`,
        { resolutions },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["import", "jobs", jobId, "dedup-candidates"],
      });
    },
  });
}

export function useSubmitImportReview(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (decision: ImportReviewDecision) => {
      return api.post<{ ok: boolean }>(
        `/import/jobs/${jobId}/review`,
        decision,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["import", "jobs"] });
      queryClient.invalidateQueries({
        queryKey: ["import", "pending-review-count"],
      });
    },
  });
}

// ADV-1402: re-validate a single corrected error row before approval. The UI
// sends the row index + edited field values; the server re-hydrates the full
// original row from input_data and returns the live pass/fail verdict. No
// cache invalidation — this is a read-only dry run that imports nothing.
export function useRevalidateImportRow(jobId: string) {
  return useMutation({
    mutationFn: async (input: ImportRevalidateRowRequest) =>
      api.post<ImportRevalidateRowResponse>(
        `/import/jobs/${jobId}/errors/revalidate`,
        input,
      ),
  });
}

export function useImportPendingReviewCount() {
  return useQuery({
    queryKey: ["import", "pending-review-count"],
    queryFn: () => api.get<{ count: number }>("/import/pending-review-count"),
    refetchInterval: 30_000,
  });
}

export function useImportEntities() {
  return useQuery({
    queryKey: ["import", "entities"],
    queryFn: () => api.get<ImportEntitiesResponse>("/import/entities"),
    staleTime: 15 * 60 * 1000,
  });
}

export function useValidateCsv() {
  return useMutation({
    mutationFn: async ({
      entityType,
      file,
    }: {
      entityType: ImportEntityType;
      file: File;
    }) => {
      const form = new FormData();
      form.append("file", file);
      return api.postMultipart<CsvValidationResponse>(
        `/import/${entityType}/validate`,
        form,
      );
    },
  });
}

// ADV-1055: `mode` was formerly `"incremental" | "full"`. `full` was an
// unfinished destructive feature and was removed from the API; the wizard no
// longer offers a choice and the server defaults the request to incremental.
export function useUploadCsv() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entityType,
      file,
      source,
    }: {
      entityType: ImportEntityType;
      file: File;
      source?: string;
    }) => {
      const form = new FormData();
      form.append("file", file);
      if (source) form.append("source", source);
      return api.postMultipart<CsvUploadResponse>(
        `/import/${entityType}/upload`,
        form,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["import", "jobs"] });
    },
  });
}

// ─── ADV-291: custom field schema + inline create + mapping persistence ─────

interface FieldSchemaResponse {
  schema: Record<string, CustomFieldDefinition>;
}

/**
 * Fetch the active custom-field schema for an entity type. Returned as a
 * map keyed by `field_key`. Used by the column-mapping step to build the
 * "Map to existing custom field" picker and to re-bucket after inline create.
 */
export function useFieldSchema(entityType: ImportEntityType | null) {
  return useQuery({
    queryKey: ["field-definitions", "schema", entityType],
    enabled: entityType !== null,
    queryFn: () =>
      api.get<FieldSchemaResponse>(
        `/core/field-definitions/schema/${entityType}`,
      ),
    staleTime: 30 * 1000,
  });
}

/**
 * Bulk-upsert custom field definitions. The column mapper calls this when
 * the user creates a new custom field inline. On success, the field schema
 * query is invalidated so the mapper immediately re-buckets the column.
 */
export function useBulkUpsertFieldDefinitions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (definitions: BulkFieldDefinitionInput[]) => {
      return api.post<{
        count: number;
        field_definitions: CustomFieldDefinition[];
      }>(`/core/field-definitions/bulk`, definitions);
    },
    onSuccess: (_data, variables) => {
      const entityTypes = new Set(variables.map((d) => d.entity_type));
      for (const et of entityTypes) {
        queryClient.invalidateQueries({
          queryKey: ["field-definitions", "schema", et],
        });
      }
    },
  });
}

/**
 * Persist user column-mapping decisions (ignore / map-to-field / core rename)
 * so the next import of the same CSV pre-fills the same bucketing. Ignored
 * auto-discovered mappings are already persisted by the upload handler;
 * this hook is only for choices the user made explicitly.
 */
/**
 * Trigger a CSV template download for the given entity type. Streams the
 * file via the api client's `download` helper. The hook returns a function
 * the caller can invoke from a button click; the returned promise resolves
 * once the browser has accepted the download (or rejects with the error).
 */
export function useDownloadCsvTemplate() {
  return useMutation({
    mutationFn: async (entityType: ImportEntityType) => {
      const blob = await api.download(`/import/templates/${entityType}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${entityType}-template.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });
}

// ADV-1108: stream the unmatched rows CSV. The endpoint is server-rendered
// (`GET /import/jobs/:id/unmatched.csv`) and includes every original row_data
// column union — the user re-imports this CSV after loading the missing parents.
export function useDownloadUnmatchedCsv() {
  return useMutation({
    mutationFn: async ({
      jobId,
      filename,
    }: {
      jobId: string;
      filename: string;
    }) => {
      const blob = await api.download(`/import/jobs/${jobId}/unmatched.csv`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });
}

export function useSaveColumnMappings() {
  return useMutation({
    mutationFn: async ({
      entityType,
      mappings,
    }: {
      entityType: ImportEntityType;
      mappings: ColumnMappingInput[];
    }) => {
      return api.post<{ count: number }>(
        `/import/${entityType}/column-mappings`,
        { mappings },
      );
    },
  });
}
